import type { AfarNetworkBridge } from "@afar-research/contracts";
import { useCallback, useEffect, useMemo, useState } from "react";

import { connectNetworkEnvironment } from "../../connection/networkMachineRegistration";
import { removeConnectEnvironmentFromDevice } from "../../connection/removeEnvironment";
import { readLocalApi } from "../../localApi";
import { describeNetworkRefusal } from "./afarNetworkSettings.logic";
import type { RememberedMachine } from "./networkMachines.logic";

/** The account bridge's network half, present only on the Runtime Web Client (ADR-0146). */
export function useNetworkBridge(): AfarNetworkBridge | undefined {
  return readLocalApi()?.connect?.networks;
}

/**
 * The network machines this device uses, as its own storage remembers them: what Connections
 * lists. Read again after a change made here and whenever the page comes back into view, since
 * another tab may have changed it.
 */
export function useRememberedMachines(bridge: AfarNetworkBridge | undefined) {
  const [version, setVersion] = useState(0);
  const changed = useCallback(() => setVersion((value) => value + 1), []);
  useEffect(() => {
    window.addEventListener("focus", changed);
    return () => window.removeEventListener("focus", changed);
  }, [changed]);
  const remembered = useMemo<ReadonlyArray<RememberedMachine>>(
    // `version` is the read's trigger, not an input.
    () => (version >= 0 ? (bridge?.rememberedMachines() ?? []) : []),
    [bridge, version],
  );
  return { remembered, changed };
}

/** Adds a network machine to this device's Connections; throws why Connect refused. */
export async function connectMachine(target: {
  readonly networkId: string;
  readonly machineId: string;
}): Promise<string> {
  const account = readLocalApi()?.connect;
  if (!account) throw new Error("The account is signed out. Sign in again.");
  const outcome = await connectNetworkEnvironment(account, target);
  if (outcome._tag === "NetworkMachineRefused") throw new Error(describeNetworkRefusal(outcome));
  return outcome.label;
}

/** Takes a machine out of this device's Connections; the machine itself is untouched. */
export async function disconnectEnvironment(environmentId: string): Promise<void> {
  const account = readLocalApi()?.connect;
  if (!account) throw new Error("The account is signed out. Sign in again.");
  await removeConnectEnvironmentFromDevice(account, environmentId);
}
